import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { errorReference, translateError } from '@rootsight/shared';

import { Button } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';

import type { ReactNode } from 'react';
import './Alert.css';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const icons: Record<AlertTone, ReactNode> = {
  info: <Info />,
  success: <CheckCircle2 />,
  warning: <AlertTriangle />,
  danger: <XCircle />,
};

export interface AlertProps {
  tone?: AlertTone;
  /** The outcome, in a few words: "Scan failed". */
  title?: string;
  /** What happened and what to do about it. */
  children?: ReactNode;
  /** A recovery step, normally a secondary Button: "Try again". */
  action?: ReactNode;
  /** Shows a close button. Leave it off for anything the operator must act on. */
  onDismiss?: () => void;
}

/**
 * Alert reports something about the page or the last action, inline, where it
 * happened. Danger alerts interrupt screen readers; the others wait their turn.
 * An alert with nothing to say renders nothing.
 */
export function Alert({ tone = 'info', title, children, action, onDismiss }: AlertProps) {
  const { t } = useTranslation('common');
  if (!title && !children) return null;
  return (
    <div className="rs-alert" data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
      <span className="rs-alert__icon" aria-hidden="true">
        {icons[tone]}
      </span>
      <div className="rs-alert__body">
        {title && <p className="rs-alert__title">{title}</p>}
        {children ? <div className="rs-alert__message">{children}</div> : null}
        {action ? <div className="rs-alert__action">{action}</div> : null}
      </div>
      {onDismiss && (
        <IconButton icon={<X />} label={t('actions.close')} size="sm" onPress={onDismiss} />
      )}
    </div>
  );
}

/** ErrorNotice is the danger Alert for a failed request: the translated
 * reason, the correlation id support will ask for, and a retry when the caller
 * can offer one. */
export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation('common');
  const reference = errorReference(error);
  return (
    <Alert
      tone="danger"
      title={translateError(t, error)}
      {...(onRetry
        ? {
            action: (
              <Button size="sm" onPress={onRetry}>
                {t('actions.retry')}
              </Button>
            ),
          }
        : {})}
    >
      {reference && (
        <span className="rs-alert__reference">
          {t('errors.requestId', { requestId: reference })}
        </span>
      )}
    </Alert>
  );
}
